<template>
  <div>
    <DataTable
      ref="dataTable"
      :columns="columnList"
      defaultColumnOrderDir="ASC"
      :defaultColumnOrderIndex="0"
      :url="dataURL"
      :filterOptions="filterOptions"
      :hasDateRangeFilter="false"
      exportTitle="Stock Transfer">
      <template v-slot:title>
        <slot name="title" />
      </template>
      <template v-slot:tools>
        <slot name="tools" />
      </template>
      <template v-slot:row="row">
        <tr :key="row.data.id">
          <td class="table-data-nav-link">
            <LinkStock :id="row.data.id" :name="row.data.code" :newTab="openNewTab" />
          </td>
          <td>
            <LinkStock :id="row.data.id" :name="row.data.name" :newTab="openNewTab" />
          </td>
          <td>
            <span :class="['label', `label-stock-${row.data.type}`]">{{row.data.type | formatLookupName(InventoryStockTypeLookup)}}</span>
          </td>
          <td>
            <FormButton class="btn-xxs btn-default mr-1" @click="addTransferClicked($event, row.data)">Add Transfer</FormButton>
          </td>
        </tr>
      </template>
    </DataTable>

    <Popover ref="stockTransferPopover" :showHeader="true" headerTitle="Add Transfer" sizeClass="popover-xlarge" :appendToBody="false" placement="left">
      <div class="p-2">
        <LoadingPanel :loading="loadingStock" :loadingError="loadingStockError" />
        <div v-if="!loadingStock && !loadingStockError">
          <div class="row">
            <div class="col-12">
              <div class="font-weight-bold">Stock</div>
              <div>{{selectedStockData.code}} | {{selectedStockData.name}}</div>
              <div class="font-weight-bold">Type</div>
              <div><span :class="['label', `label-stock-${selectedStockData.type}`]">{{selectedStockData.type | formatLookupName(InventoryStockTypeLookup)}}</span></div>
            </div>
          </div>
          <FormHorizontalRule />
          <div class="row" v-if="isSelectedStockGlass">
            <div class="col-12">
              <div class="font-weight-bold">Size</div>
              <div>
                <FormSelect :options="glassLevelLookup" v-model="transferGlassLevelId" />
              </div>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12">
              <div class="font-weight-bold">Quantity</div>
              <div>
                <FormInputText type="number" placeholder="Quantity" v-model="transferQuantity" />
              </div>
            </div>
          </div>
          <FormHorizontalRule />
          <div class="row mt-2">
            <div class="col-12">
              <div class="font-weight-bold">Transfer Type</div>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12">
              <FormOption label="Transfer To" name="transferLocationType" setting="delivery" v-model="transferLocationType" inline="true" />
              <FormOption label="Collect From" name="transferLocationType" setting="pickup" v-model="transferLocationType" inline="true" />
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12">
              <div>
                <AccountLocationSelect v-model="transferAccountLocationId" field="accountLocationId" />
              </div>
              <div>
                <small v-if="isLoadedAtLocation">Stock will be picked up at <strong><AccountLocationDisplay :id="transferAccountLocationId" /></strong></small>
                <small v-if="!isLoadedAtLocation">Stock will be loaded prior to transport and delivered to <strong><AccountLocationDisplay :id="transferAccountLocationId" /></strong></small>
              </div>
            </div>
          </div>
          <FormHorizontalRule />
          <div class="row mt-2">
            <div class="col-12">
              <FormButton @click="addStockTransfer" class="btn-sm btn-default mr-1" :disabled="!isAddTransferEnabled"><i class="far fa-plus-circle" /> Add Transfer</FormButton>
            </div>
          </div>
        </div>
      </div>
    </Popover>
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Popover from '@/components/popover'

import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'
import AccountLocationDisplay from '@/modules/admin/components/accountLocationDisplay'

import { TaskLinkedItemTypes, InventoryStockTypes, InventoryStockTypeLookup } from '@/store/types'

export default {
  name: 'TaskStockTransferDataTable',
  model: {
    event: 'update'
  },
  mixins: [form],
  components: {
    Popover,
    AccountLocationSelect,
    AccountLocationDisplay
  },
  props: {
    openNewTab: {
      type: Boolean,
      default: false
    },
    value: Array,
    filterAccountLocationId: Number,
    selectionEnabled: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {
          isTrackingQuantity: 1
        }
      }

      return '/inventory/stock?' + $.param(params)
    },
    isSelectedStockGlass() {
      if(!this.selectedStockData.id)
        return false

      return this.selectedStockData.type == InventoryStockTypes.glass
    },
    isAddTransferEnabled() {
      if(this.isSelectedStockGlass && this.transferGlassLevelId == 0)
        return false
      return parseInt(this.transferQuantity || 0) > 0
    },
    isLoadedAtLocation() {
      return this.transferLocationType == 'pickup'
    }
  },
  data() {
    let columnList = _.compact([
      { title: "Code", data: "code" },
      { title: "Name", data: "name" },
      { title: "Type", data: "type" },
      this.selectionEnabled ? { title: "Add Runsheet", data: "", sortable: false, export: false } : null,
    ])

    let filterOptions = [
      {
        field: 'type',
        name: 'Stock Type',
        options: [
          { name: 'Glass', value: 'glass', class: 'label-stock-glass' },
          { name: 'General', value: 'general', class: 'label-stock-general' },
        ]
      },
    ]

    return {
      filterOptions: filterOptions,
      columnList: columnList,
      selectedStockData: {},
      transferLocationType: 'delivery',
      transferQuantity: 0,
      transferAccountLocationId: 0,
      transferGlassLevelId: 0,
      glassLevelList: [],
      loadingStock: false,
      loadingStockError: ''
    }
  },
  created() {
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
  },
  mounted() {
    // Apply default filters
    this.$nextTick(() => {
      let defaultFilters = _.compact([
        { value: InventoryStockTypes.general, field: 'type' },
        { value: InventoryStockTypes.glass, field: 'type' },
      ])

      this.$refs.dataTable.applySelectedFilters(defaultFilters)
    })
    
  },
  methods: {
    addTransferClicked(ev, stock) {
      this.$refs.stockTransferPopover.displayFromElement(ev.target)

      // Load stock details
      this.loadingStock = true
      this.loadingStockError = ''
      this.transferQuantity = 0
      this.transferAccountLocationId = 0
      this.transferGlassLevelId = 0
      this.glassLevelLookup = []

      this.$apiService.inventoryService.getStock(stock.id)
      .then(response => {
        this.selectedStockData = response.data

        if(_.isArray(this.selectedStockData.glassSizeList)) {
          this.glassLevelLookup = this.selectedStockData.glassSizeList.reduce((list, glassSize) => {
            if(!glassSize.isActive || glassSize.glassLevels.length == 0)
              return list
            list.push({
              name: `${glassSize.height}x${glassSize.width}`,
              children: glassSize.glassLevels.filter(glassLevel => !glassLevel.isOffcut).map(glassLevel => {
                return { value: glassLevel.id, name: `${glassLevel.height}x${glassLevel.width}` }
              })
            })
            return list
          }, [])
        }
      })
      .catch(err => {
        this.loadingStockError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loadingStock = false
      })
    },
    addStockTransfer() {
      this.$refs.stockTransferPopover.hidePopover()

      var glassLevel = null
      var type = TaskLinkedItemTypes.general
      
      if(this.isSelectedStockGlass) {
        type = TaskLinkedItemTypes.glassLevel
        this.selectedStockData.glassSizeList.forEach(glassSize => {
          glassSize.glassLevels.forEach(level => {
            if(level.id == this.transferGlassLevelId) {
              glassLevel = level
            }
          })
        })
      }

      this.$emit('stockTransferAdded', {
        type: type,
        stockId: this.selectedStockData.id,
        stock: this.selectedStockData,
        isLoadedAtLocation: this.isLoadedAtLocation,
        accountLocationId: parseInt(this.transferAccountLocationId),
        glassLevelId: parseInt(this.transferGlassLevelId),
        glassLevel: glassLevel,
        quantity: parseFloat(this.transferQuantity)
      })
    }

  }
};
</script>
